import { reportBelongsToSite } from '../lib/reportSiteIdentity';
/**
 * SED Loader — loads Site Evidence Documents from GCS for reuse across report types.
 */
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import { auth, db } from '../firebase';
import { authedFetch } from '../lib/authedFetch';
import { awaitAbortableRead, throwIfReadAborted } from '../lib/abortableRead';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Resident SED bodies, least recently used first. A SED can run to several
 * megabytes and every site the portfolio recompute touches used to leave its
 * document here for the life of the tab. The session-storage tier below is
 * unaffected by eviction: a document that falls out of memory is re-admitted
 * from there on its next read without a network round trip.
 */
export const SED_MEMORY_CACHE_LIMIT = 8;
const SED_MEMORY_CACHE = new Map<string, SiteEvidenceDocument>();
const SED_GENERATION_CACHE = new Map<string, string>();
interface PendingStageEnvelope {
  candidateOperationId: string;
  pubOperationId: string;
  ownerUid: string;
  siteId: string;
  stagingTargetPath: string;
  baselinePointer: {
    sedId: string | null;
    sedStoragePath: string | null;
    sedVersion: number | null;
    sedContentHash: string | null;
  };
  ifGenerationMatch?: string;
  cleanSed: SiteEvidenceDocument;
  candidateContentHash: string;
  statusPatch?: Record<string, unknown>;
  requireIdle?: boolean;
}
const PENDING_STAGE_ENVELOPES = new Map<string, PendingStageEnvelope>();
const SED_SESSION_PREFIX = 'da-sed-loader:';

export function getCachedSedGeneration(sedIdOrPath: string | null | undefined): string | undefined {
  if (!sedIdOrPath) return undefined;
  return SED_GENERATION_CACHE.get(sedIdOrPath);
}

/** Insert or refresh `key` as the most recently used entry, evicting the oldest past the limit. */
function admitSedToMemory(key: string, sed: SiteEvidenceDocument): void {
  SED_MEMORY_CACHE.delete(key);
  SED_MEMORY_CACHE.set(key, sed);
  while (SED_MEMORY_CACHE.size > SED_MEMORY_CACHE_LIMIT) {
    const oldest = SED_MEMORY_CACHE.keys().next();
    if (oldest.done) break;
    SED_MEMORY_CACHE.delete(oldest.value);
  }
}

function sedCacheKey(uid: string, sedId: string, storagePath?: string, version?: number): string {
  return `${uid}::${sedId}::${storagePath ?? ''}::${version ?? ''}`;
}

function readSedFromSession(key: string): SiteEvidenceDocument | undefined {
  if (typeof sessionStorage === 'undefined') return undefined;
  try {
    const raw = sessionStorage.getItem(`${SED_SESSION_PREFIX}${key}`);
    if (!raw) return undefined;
    return JSON.parse(raw) as SiteEvidenceDocument;
  } catch {
    return undefined;
  }
}

function writeSedToSession(key: string, sed: SiteEvidenceDocument): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(`${SED_SESSION_PREFIX}${key}`, JSON.stringify(sed));
  } catch {
    // Large SED payloads may exceed quota — memory cache still helps SPA navigation.
  }
}

function matchesRequestedSed(sed: unknown, sedId: string, version?: number): boolean {
  if (!sed || typeof sed !== 'object' || !('id' in sed) || sed.id !== sedId) return false;
  return version == null || ('version' in sed && sed.version === version);
}

/** Evict only the rejected owner/path/version entry; other aliases stay intact. */
function forgetSedKey(key: string): void {
  SED_MEMORY_CACHE.delete(key);
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(`${SED_SESSION_PREFIX}${key}`);
  } catch {
    // Storage access may be disabled; the rejected body is still not returned.
  }
}

/** Synchronous cache read for initial render (memory → sessionStorage). */
export function getCachedSedById(
  sedId: string,
  storagePath?: string,
  version?: number,
): SiteEvidenceDocument | undefined {
  const user = auth.currentUser;
  if (!user || !sedId) return undefined;
  const key = sedCacheKey(user.uid, sedId, storagePath, version);
  const mem = SED_MEMORY_CACHE.get(key);
  if (mem) {
    if (matchesRequestedSed(mem, sedId, version)) {
      admitSedToMemory(key, mem); // a hit is a use: keep the open site's SED resident
      return mem;
    }
    forgetSedKey(key);
    return undefined;
  }
  const fromSession = readSedFromSession(key);
  if (fromSession) {
    if (!matchesRequestedSed(fromSession, sedId, version)) {
      forgetSedKey(key);
      return undefined;
    }
    admitSedToMemory(key, fromSession);
    return fromSession;
  }
  return undefined;
}

function rememberSed(uid: string, sedId: string, storagePath: string | undefined, version: number | undefined, sed: SiteEvidenceDocument): void {
  const key = sedCacheKey(uid, sedId, storagePath, version);
  admitSedToMemory(key, sed);
  writeSedToSession(key, sed);
}

/** Drop every path/version alias for one user-owned SED. */
export function invalidateCachedSed(
  sedId: string,
  _storagePath?: string,
  _version?: number,
): void {
  if (sedId) {
    SED_GENERATION_CACHE.delete(sedId);
  }
  const user = auth.currentUser;
  if (!user || !sedId) return;
  const keyPrefix = `${user.uid}::${sedId}::`;
  for (const key of SED_MEMORY_CACHE.keys()) {
    if (key.startsWith(keyPrefix)) SED_MEMORY_CACHE.delete(key);
  }
  if (typeof sessionStorage === 'undefined') return;
  try {
    const storagePrefix = `${SED_SESSION_PREFIX}${keyPrefix}`;
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(storagePrefix)) sessionStorage.removeItem(key);
    }
  } catch {
    /* swallow */
  }
}

/** Test hook — reset module caches. */
export function getCachedSedStorageGeneration(
  sedId: string,
  storagePath?: string | null,
): string | undefined {
  if (storagePath && SED_GENERATION_CACHE.has(storagePath)) {
    return SED_GENERATION_CACHE.get(storagePath);
  }
  if (sedId && SED_GENERATION_CACHE.has(sedId)) {
    return SED_GENERATION_CACHE.get(sedId);
  }
  return undefined;
}

export function primeCommittedSedFromServer(input: {
  sed: SiteEvidenceDocument;
  sedId: string;
  storagePath: string;
  version: number;
  generation?: string | null;
  preserveStoragePaths?: readonly string[];
}): void {
  const preserve = new Set<string>([
    input.storagePath,
    ...(input.preserveStoragePaths ?? []).filter((p): p is string => Boolean(p)),
  ]);
  invalidateMutableSedCacheAliases(input.sedId, preserve);
  if (input.generation && input.generation.trim()) {
    const gen = input.generation.trim();
    SED_GENERATION_CACHE.set(input.storagePath, gen);
    SED_GENERATION_CACHE.set(input.sedId, gen);
  }
  const user = auth.currentUser;
  if (user?.uid && input.sed) {
    rememberSed(user.uid, input.sedId, input.storagePath, input.version, input.sed);
    rememberSed(user.uid, input.sedId, input.storagePath, undefined, input.sed);
    rememberSed(user.uid, input.sedId, undefined, input.version, input.sed);
    rememberSed(user.uid, input.sedId, undefined, undefined, input.sed);
  }
}

export function clearSedLoaderCacheForTests(): void {
  SED_MEMORY_CACHE.clear();
  SED_GENERATION_CACHE.clear();
  PENDING_STAGE_ENVELOPES.clear();
  if (typeof sessionStorage === 'undefined') return;
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(SED_SESSION_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch {
    /* swallow */
  }
}

/**
 * Load a SED by its ID from GCS storage.
 *
 * Path resolution: SEDs written by the current bootstrap pipeline
 * land at a site-nested path (`users/{uid}/sites/{siteId}/evidence/
 * {sedId}.json` — see sedBootstrap.gcsStoragePath). Older SEDs lived
 * at the flat `users/{uid}/evidence/{sedId}.json` path. Callers that
 * have access to `site.sedStoragePath` should pass it; otherwise we
 * fall back to the legacy flat path for back-compat.
 *
 * Returns null if not found or user not authenticated.
 */
export async function loadSedById(
  sedId: string,
  storagePath?: string,
  version?: number,
  options?: { signal?: AbortSignal },
): Promise<SiteEvidenceDocument | null> {
  const signal = options?.signal;
  throwIfReadAborted(signal);
  const user = auth.currentUser;
  if (!user || !sedId) return null;

  const cached = getCachedSedById(sedId, storagePath, version);
  if (cached) return cached;

  // Try paths in order: caller-provided (if any), then the legacy flat
  // path. A 200 must match the requested id and explicit version; other
  // responses return null without throwing after both paths are exhausted.
  const candidates: string[] = [];
  if (storagePath) candidates.push(storagePath);
  // The legacy flat path belongs to the SITE OWNER, not the reader: an operator opening an owner's
  // site would otherwise probe their own users/{uid}/evidence and get a 404.
  const ownerUid = /^users\/([^/]+)\//.exec(storagePath ?? '')?.[1] ?? user.uid;
  candidates.push(`users/${ownerUid}/evidence/${sedId}.json`);

  // A DENIAL and an ABSENCE both end as `null` here, and that is the whole
  // difficulty: callers surface "SED {id} could not be loaded" either way, so
  // an authorization gap on a company-shared site reads to the operator as a
  // missing file. Record the distinction. The return contract is unchanged —
  // every caller still gets `null` — but the console now says WHICH failure
  // happened, on WHICH candidate path.
  let deniedPath: string | undefined;
  for (const path of candidates) {
    try {
      throwIfReadAborted(signal);
      const url = `/api/storage/load?path=${encodeURIComponent(path)}`;
      const request = signal ? authedFetch(url, { signal }) : authedFetch(url);
      const res = await awaitAbortableRead(request, signal);
      if (res.ok) {
        const data = await awaitAbortableRead(res.json(), signal) as SiteEvidenceDocument;
        // A cancelled/stale read must never prime a cache after its view left.
        throwIfReadAborted(signal);
        if (!matchesRequestedSed(data, sedId, version)) {
          // Do not cache a rejected 200 under the requested identity. A normal
          // Reload can then read repaired storage instead of replaying poison.
          console.warn('[sedLoader] rejected a body with a different SED id or requested version');
          continue;
        }
        const loadedGen = res.headers?.get?.('x-storage-generation')?.trim();
        if (loadedGen) {
          SED_GENERATION_CACHE.set(path, loadedGen);
          SED_GENERATION_CACHE.set(sedId, loadedGen);
          if (storagePath) SED_GENERATION_CACHE.set(storagePath, loadedGen);
        }
        rememberSed(user.uid, sedId, storagePath, version, data);
        return data;
      }
      if (res.status === 403 || res.status === 401) {
        deniedPath = path;
        console.warn(
          `[sedLoader] DENIED (${res.status}) reading ${path} — not an absent file. `
          + 'On a company-shared site this is the cross-org read gate; the caller will '
          + 'report the SED as unloadable.',
        );
      }
    } catch (e) {
      // Cancellation is not absence, denial, or a reason to try another path.
      throwIfReadAborted(signal);
      console.warn('[sedLoader] load failed for', path, '—', (e as Error)?.message);
    }
  }
  if (deniedPath) {
    console.warn(
      `[sedLoader] returning null for sed ${sedId} after a denial on ${deniedPath} — `
      + 'treat as an access problem, NOT a missing document.',
    );
  }
  return null;
}

/**
 * Check if a SED is stale (older than maxAge).
 * Default: 7 days.
 */
export function isSedStale(sed: SiteEvidenceDocument, maxAgeMs: number = 7 * 24 * 60 * 60 * 1000): boolean {
  const collected = new Date(sed.collectedAt).getTime();
  return isNaN(collected) || Date.now() - collected > maxAgeMs;
}

/**
 * UD2 — load the prior SED for a site (if any), used by sedBootstrap
 * to overlay user decisions across rebuilds.
 *
 * Reads the site doc to discover `sedId` + `sedStoragePath` (set by
 * `bootstrapSiteSed`), then defers to `loadSedById`. Returns `null`
 * when:
 *   - the user isn't authenticated
 *   - the site doc doesn't exist
 *   - the site has no sedId (first-time bootstrap)
 *   - the GCS read fails (no prior payload to overlay)
 *
 * Defensive: every failure path returns `null` so the bootstrap
 * caller can safely treat "no prior" and "load failed" identically
 * (both => fresh items overwrite, no preservation possible).
 */
export async function loadPriorSedForSite(
  siteId: string,
): Promise<SiteEvidenceDocument | null> {
  const user = auth.currentUser;
  if (!user || !siteId) return null;
  try {
    const siteRef = doc(db, 'users', user.uid, 'sites', siteId);
    const snap = await getDoc(siteRef);
    if (!snap.exists()) return null;
    const siteData = snap.data() as { sedId?: string; sedStoragePath?: string } | undefined;
    const sedId = siteData?.sedId;
    if (!sedId) return null;
    return await loadSedById(sedId, siteData?.sedStoragePath);
  } catch (e) {
    console.warn('[sedLoader] loadPriorSedForSite failed:', (e as Error)?.message);
    return null;
  }
}

/**
 * LLW-FOLLOWUP-SECTION-REFRESH-BUTTON — persist a SED back to GCS at
 * the canonical site-nested path.
 *
 * Mirror of `sedBootstrap.saveSedToGcs` exposed for callers that own
 * the read+mutate+save loop without re-using `topUpSiteSed`'s wider
 * pipeline (e.g. the per-section "Refresh Live Literature" store
 * action only mutates `sed.externalLiterature`, so the full topUp
 * compile is unnecessary).
 *
 * Strips non-finite numbers / undefined / functions so the server-
 * side JSON encode doesn't blow up — same guard the bootstrap path
 * applies. Returns `false` on transport failure so the caller can
 * surface a toast without throwing.
 *
 * Path resolution is identical to `loadSedById`: caller-supplied
 * `storagePath` wins; otherwise we fall back to the legacy flat
 * `users/{uid}/evidence/{sedId}.json`.
 */
export interface StageAndPublishSedInput {
  sed: SiteEvidenceDocument;
  storagePath?: string;
  siteId?: string;
  ownerUid?: string;
  operationId?: string;
  ifGenerationMatch?: string;
  statusPatch?: Record<string, unknown>;
  requireIdle?: boolean;
}

export interface StageAndPublishSedResult {
  ok: boolean;
  storagePath: string;
  version: number;
  generation?: string;
  sed: SiteEvidenceDocument;
  status?: number;
  error?: string;
}

/**
 * Shared stage + publish helper for SED mutations that go through
 * `POST /api/storage/save` (staging only) followed by
 * `POST /api/sites/:siteId/sed-pointer-cas` (authoritative publication).
 *
 * Never invalidates or primes the SED cache on staging success alone, and
 * requires `committed: true` from pointer publication before reporting success.
 */
function invalidateMutableSedCacheAliases(
  sedId: string,
  preserveRevisionPaths: ReadonlySet<string>,
): void {
  if (sedId) {
    SED_GENERATION_CACHE.delete(sedId);
  }
  for (const genKey of [...SED_GENERATION_CACHE.keys()]) {
    if (genKey === sedId) continue;
    if (preserveRevisionPaths.has(genKey)) continue;
    if (/^users\/[^/]+\/sites\/[^/]+\/evidence\/revisions\//.test(genKey)) {
      continue;
    }
    if (genKey.endsWith(`/${sedId}.json`)) {
      SED_GENERATION_CACHE.delete(genKey);
    }
  }
  const user = auth.currentUser;
  if (!user || !sedId) return;
  const keyPrefix = `${user.uid}::${sedId}::`;
  for (const key of [...SED_MEMORY_CACHE.keys()]) {
    if (!key.startsWith(keyPrefix)) continue;
    const rest = key.slice(keyPrefix.length);
    const sepIdx = rest.lastIndexOf('::');
    const cachedPath = sepIdx >= 0 ? rest.slice(0, sepIdx) : rest;
    if (
      cachedPath &&
      (preserveRevisionPaths.has(cachedPath) ||
        /^users\/[^/]+\/sites\/[^/]+\/evidence\/revisions\//.test(cachedPath))
    ) {
      continue;
    }
    SED_MEMORY_CACHE.delete(key);
  }
  if (typeof sessionStorage === 'undefined') return;
  try {
    const storagePrefix = `${SED_SESSION_PREFIX}${keyPrefix}`;
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (!key?.startsWith(storagePrefix)) continue;
      const rest = key.slice(storagePrefix.length);
      const sepIdx = rest.lastIndexOf('::');
      const cachedPath = sepIdx >= 0 ? rest.slice(0, sepIdx) : rest;
      if (
        cachedPath &&
        (preserveRevisionPaths.has(cachedPath) ||
          /^users\/[^/]+\/sites\/[^/]+\/evidence\/revisions\//.test(cachedPath))
      ) {
        continue;
      }
      sessionStorage.removeItem(key);
    }
  } catch {
    /* swallow */
  }
}

export async function stageAndPublishSedDocument(
  input: StageAndPublishSedInput,
): Promise<StageAndPublishSedResult> {
  const user = auth.currentUser;
  const fallbackUid = input.ownerUid?.trim() || user?.uid || '';
  if (!fallbackUid || !input.sed?.id) {
    return {
      ok: false,
      storagePath: input.storagePath ?? '',
      version: input.sed?.version ?? 1,
      sed: input.sed,
      error: 'Missing authenticated user or SED id.',
    };
  }
  const rawInputPath = input.storagePath?.trim() || undefined;
  const cleanSed = JSON.parse(JSON.stringify(input.sed, (_, v) => {
    if (typeof v === 'number' && !isFinite(v)) return null;
    if (v === undefined) return null;
    if (typeof v === 'function') return null;
    return v;
  })) as SiteEvidenceDocument;

  const {
    parseSedRevisionStoragePath,
    sedObjectPath,
  } = await import('../lib/sed/sedStoragePath');

  const nestedMatch = rawInputPath
    ? /^users\/([^/]+)\/sites\/([^/]+)\/evidence\//.exec(rawInputPath)
    : null;
  const flatMatch = rawInputPath
    ? /^users\/([^/]+)\/evidence\//.exec(rawInputPath)
    : null;

  type StoreSedSlice = {
    siteRag?: { sites?: Array<Record<string, unknown>> };
    readLiveSedHead?: (siteId: string) => Promise<{
      pointer: {
        sedId: string | null;
        sedStoragePath: string | null;
        sedVersion: number | null;
        sedContentHash: string | null;
      };
      buildInProgress: boolean;
    } | null>;
    commitSedPointerIfUnchanged?: (
      siteId: string,
      expected: {
        sedId: string | null;
        sedStoragePath: string | null;
        sedVersion: number | null;
        sedContentHash: string | null;
      },
      patch: Record<string, unknown>,
      opts?: Record<string, unknown>,
    ) => Promise<{
      committed: boolean;
      reason?: string;
      sedId?: string | null;
      sedVersion?: number | null;
      sedStoragePath?: string | null;
      sedStorageGeneration?: string | null;
      sedContentHash?: string | null;
      sedContentHashVersion?: number | null;
      sedCollectedAt?: string | null;
      historyVersion?: number;
      updatedAt?: string;
    }>;
  };
  let storeState: StoreSedSlice | null = null;
  try {
    const storeMod = await import('../store');
    storeState = (storeMod.useStore?.getState?.() as unknown as StoreSedSlice | null) ?? null;
  } catch {
    storeState = null;
  }

  const sites = Array.isArray(storeState?.siteRag?.sites) ? storeState!.siteRag!.sites! : [];
  const targetSite =
    (input.siteId ? sites.find((s) => s.id === input.siteId) : undefined) ??
    (nestedMatch?.[2] ? sites.find((s) => s.id === nestedMatch[2]) : undefined) ??
    (rawInputPath ? sites.find((s) => s.sedStoragePath === rawInputPath) : undefined) ??
    sites.find((s) => s.sedId === cleanSed.id);

  const resolvedSiteId =
    input.siteId?.trim() ||
    (typeof targetSite?.id === 'string' ? targetSite.id : undefined) ||
    nestedMatch?.[2] ||
    undefined;
  const resolvedOwnerUid =
    (typeof targetSite?._ownerUid === 'string' && targetSite._ownerUid.trim()
      ? targetSite._ownerUid.trim()
      : undefined) ||
    nestedMatch?.[1] ||
    flatMatch?.[1] ||
    input.ownerUid?.trim() ||
    user?.uid ||
    fallbackUid;

  const siteBaselinePath =
    typeof targetSite?.sedStoragePath === 'string' && targetSite.sedStoragePath.trim()
      ? targetSite.sedStoragePath.trim()
      : undefined;
  const baselineStoragePath =
    siteBaselinePath ??
    rawInputPath ??
    (resolvedSiteId
      ? sedObjectPath(resolvedOwnerUid, resolvedSiteId, cleanSed.id)
      : `users/${resolvedOwnerUid}/evidence/${cleanSed.id}.json`);

  // Separate baseline pointer from successor staging write target:
  // If the caller or site pointer names an immutable revision object, the
  // staging write target sent to /api/storage/save is the canonical nested
  // path `users/{ownerUid}/sites/{siteId}/evidence/{sedId}.json`.
  const revisionDescriptor = parseSedRevisionStoragePath(
    rawInputPath ?? baselineStoragePath,
    resolvedSiteId,
  );
  const stagingTargetPath = revisionDescriptor
    ? sedObjectPath(revisionDescriptor.ownerUid, revisionDescriptor.siteId, cleanSed.id)
    : (rawInputPath ??
      (resolvedSiteId
        ? sedObjectPath(resolvedOwnerUid, resolvedSiteId, cleanSed.id)
        : `users/${resolvedOwnerUid}/evidence/${cleanSed.id}.json`));

  if (!resolvedSiteId) {
    console.warn(`[sedLoader] cannot stage/publish SED ${cleanSed.id} without siteId (path=${stagingTargetPath})`);
    return {
      ok: false,
      storagePath: stagingTargetPath,
      version: cleanSed.version,
      sed: cleanSed,
      error: 'Cannot publish staged SED candidate without a siteId.',
    };
  }

  const { computeSedContentHashV2, computeSedSectionHashMirrors } = await import(
    '../lib/decisionAnalysis/sedContentIdentity'
  );
  const { sedPointerOf } = await import('../lib/sed/sedPointerCas');

  // Resolve authoritative baseline pointer from site state or verified resident cache
  // (never guessing `cleanSed.version - 1` or inferring missing head from object 404).
  let baselinePointer: {
    sedId: string | null;
    sedStoragePath: string | null;
    sedVersion: number | null;
    sedContentHash: string | null;
  } | null = null;

  if (typeof storeState?.readLiveSedHead === 'function') {
    try {
      const liveHead = await storeState.readLiveSedHead(resolvedSiteId);
      if (liveHead?.pointer) {
        baselinePointer = liveHead.pointer;
      }
    } catch {
      // Fall through to site state / Firestore doc.
    }
  }

  if (!baselinePointer && targetSite) {
    baselinePointer = sedPointerOf(targetSite);
  }

  if (!baselinePointer && db && typeof getDoc === 'function' && typeof doc === 'function') {
    try {
      const siteSnap = await getDoc(doc(db, 'users', resolvedOwnerUid, 'sites', resolvedSiteId));
      if (siteSnap && typeof siteSnap.exists === 'function' && siteSnap.exists()) {
        const siteData = siteSnap.data() as Record<string, unknown> | undefined;
        if (siteData) {
          baselinePointer = sedPointerOf(siteData);
        }
      }
    } catch {
      // Fall through to verified resident cache.
    }
  }

  if (!baselinePointer && baselineStoragePath) {
    const cacheCheckUid = user?.uid || resolvedOwnerUid;
    for (const [k, memSed] of SED_MEMORY_CACHE.entries()) {
      if (
        k.startsWith(`${cacheCheckUid}::${cleanSed.id}::${baselineStoragePath}::`) &&
        typeof memSed?.version === 'number'
      ) {
        baselinePointer = {
          sedId: memSed.id,
          sedStoragePath: baselineStoragePath,
          sedVersion: memSed.version,
          sedContentHash: computeSedContentHashV2(memSed),
        };
        break;
      }
    }
  }

  const candidateContentHash = computeSedContentHashV2(cleanSed);
  const explicitOpId = input.operationId?.trim();
  const retryEnvelopeKey = explicitOpId
    ? `op:${explicitOpId}`
    : JSON.stringify([
        resolvedOwnerUid,
        resolvedSiteId,
        stagingTargetPath,
        baselinePointer?.sedId ?? null,
        baselinePointer?.sedVersion ?? null,
        baselinePointer?.sedStoragePath ?? null,
        cleanSed.id,
        cleanSed.version,
        candidateContentHash,
      ]);
  const existingEnvelope = PENDING_STAGE_ENVELOPES.get(retryEnvelopeKey);
  if (
    existingEnvelope &&
    (existingEnvelope.ownerUid !== resolvedOwnerUid ||
      existingEnvelope.siteId !== resolvedSiteId ||
      existingEnvelope.cleanSed.id !== cleanSed.id ||
      existingEnvelope.candidateContentHash !== candidateContentHash)
  ) {
    return {
      ok: false,
      storagePath: stagingTargetPath,
      version: cleanSed.version,
      sed: cleanSed,
      status: 409,
      error: 'Operation ID is already bound to a different SED candidate payload.',
    };
  }
  if (!baselinePointer && existingEnvelope) {
    baselinePointer = existingEnvelope.baselinePointer;
  }

  if (!baselinePointer) {
    return {
      ok: false,
      storagePath: stagingTargetPath,
      version: cleanSed.version,
      sed: cleanSed,
      error: 'Authoritative baseline SED pointer could not be determined.',
    };
  }

  const ifGenerationMatch =
    existingEnvelope?.ifGenerationMatch ??
    input.ifGenerationMatch ??
    (baselineStoragePath ? SED_GENERATION_CACHE.get(baselineStoragePath) : undefined) ??
    (rawInputPath ? SED_GENERATION_CACHE.get(rawInputPath) : undefined) ??
    SED_GENERATION_CACHE.get(stagingTargetPath) ??
    SED_GENERATION_CACHE.get(cleanSed.id) ??
    (typeof targetSite?.sedStorageGeneration === 'string' && targetSite.sedStorageGeneration.trim()
      ? targetSite.sedStorageGeneration.trim()
      : undefined);

  const candidateOperationId =
    existingEnvelope?.candidateOperationId ||
    explicitOpId ||
    `sed-stage-${resolvedSiteId}-${cleanSed.id}-v${cleanSed.version ?? 1}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(
      /[^A-Za-z0-9._:-]/g,
      '-',
    );
  const pubOperationId =
    existingEnvelope?.pubOperationId || `pub-${candidateOperationId}`;
  const activeEnvelope: PendingStageEnvelope = existingEnvelope ?? {
    candidateOperationId,
    pubOperationId,
    ownerUid: resolvedOwnerUid,
    siteId: resolvedSiteId,
    stagingTargetPath,
    baselinePointer,
    ...(ifGenerationMatch ? { ifGenerationMatch } : {}),
    cleanSed,
    candidateContentHash,
    ...(input.statusPatch ? { statusPatch: input.statusPatch } : {}),
    ...(input.requireIdle !== undefined ? { requireIdle: input.requireIdle } : {}),
  };
  PENDING_STAGE_ENVELOPES.set(retryEnvelopeKey, activeEnvelope);

  const clearEnvelopeIfTerminal = (status?: number) => {
    if (status !== undefined && status >= 400 && status < 500) {
      PENDING_STAGE_ENVELOPES.delete(retryEnvelopeKey);
    }
  };

  try {
    const res = await authedFetch('/api/storage/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        path: stagingTargetPath,
        data: cleanSed,
        operationId: candidateOperationId,
        ...(ifGenerationMatch ? { ifGenerationMatch } : {}),
      }),
    });
    if (!res.ok) {
      clearEnvelopeIfTerminal(res.status);
      const body = await res.text().catch(() => '');
      console.warn(`[sedLoader] save failed (${res.status}) for ${stagingTargetPath}: ${body.slice(0, 200)}`);
      return {
        ok: false,
        storagePath: stagingTargetPath,
        version: cleanSed.version,
        sed: cleanSed,
        status: res.status,
        error: `Storage write failed (HTTP ${res.status}): ${body.slice(0, 200)}`,
      };
    }

    let parsedStage: {
      path?: unknown;
      stagedPath?: unknown;
      operationId?: unknown;
      generation?: unknown;
      sedId?: unknown;
      sedVersion?: unknown;
      sedContentHash?: unknown;
      sectionHashes?: unknown;
    } | null = null;
    try {
      const rawText = typeof res.text === 'function' ? await res.text() : '';
      parsedStage = rawText
        ? JSON.parse(rawText)
        : typeof res.json === 'function'
          ? await res.json()
          : null;
    } catch {
      try {
        parsedStage = typeof res.json === 'function' ? await res.json() : null;
      } catch {
        parsedStage = null;
      }
    }

    const stagedPath =
      typeof parsedStage?.stagedPath === 'string' && parsedStage.stagedPath.trim()
        ? parsedStage.stagedPath.trim()
        : typeof parsedStage?.path === 'string' && parsedStage.path.trim()
          ? parsedStage.path.trim()
          : null;
    const stagedOperationId =
      typeof parsedStage?.operationId === 'string' && parsedStage.operationId.trim()
        ? parsedStage.operationId.trim()
        : null;
    const stagedGeneration =
      typeof parsedStage?.generation === 'string' &&
      /^[1-9][0-9]*$/.test(parsedStage.generation.trim())
        ? parsedStage.generation.trim()
        : null;

    const stagedSedId =
      typeof parsedStage?.sedId === 'string' && parsedStage.sedId.trim()
        ? parsedStage.sedId.trim()
        : null;
    const stagedVersion =
      typeof parsedStage?.sedVersion === 'number' &&
      Number.isFinite(parsedStage.sedVersion) &&
      parsedStage.sedVersion >= 1
        ? parsedStage.sedVersion
        : null;
    const stagedContentHash =
      typeof parsedStage?.sedContentHash === 'string' && parsedStage.sedContentHash.trim()
        ? parsedStage.sedContentHash.trim()
        : null;

    if (
      !stagedPath ||
      !stagedOperationId ||
      stagedOperationId !== candidateOperationId ||
      !stagedGeneration ||
      stagedSedId !== cleanSed.id ||
      stagedVersion === null ||
      !stagedContentHash
    ) {
      console.warn(`[sedLoader] malformed staging receipt for ${stagingTargetPath}`);
      return {
        ok: false,
        storagePath: stagingTargetPath,
        version: cleanSed.version,
        sed: cleanSed,
        status: 502,
        error: 'Malformed or incomplete staging receipt from /api/storage/save.',
      };
    }

    const stagedSectionHashes =
      parsedStage?.sectionHashes &&
      typeof parsedStage.sectionHashes === 'object' &&
      !Array.isArray(parsedStage.sectionHashes)
        ? (parsedStage.sectionHashes as Record<string, string>)
        : computeSedSectionHashMirrors(cleanSed);

    const pointerPatch: Record<string, unknown> = {
      sedId: cleanSed.id,
      sedVersion: stagedVersion,
      sedStoragePath: stagedPath,
      sedStorageGeneration: stagedGeneration,
      sedContentHash: stagedContentHash,
      ...stagedSectionHashes,
      sedContentHashVersion: stagedVersion,
      sedCollectedAt: cleanSed.collectedAt,
    };

    let committedPath: string;
    let committedVersion: number;
    let committedGeneration: string;
    let committedContentHash: string;

    if (targetSite && typeof storeState?.commitSedPointerIfUnchanged === 'function') {
      const committed = await storeState.commitSedPointerIfUnchanged(
        resolvedSiteId,
        baselinePointer,
        pointerPatch,
        {
          ...(input.statusPatch ? { statusPatch: input.statusPatch } : {}),
          ...(input.requireIdle !== undefined ? { requireIdle: input.requireIdle } : {}),
          candidateOperationId: stagedOperationId,
          operationId: pubOperationId,
        },
      );
      if (!committed?.committed) {
        PENDING_STAGE_ENVELOPES.delete(retryEnvelopeKey);
        console.warn(
          `[sedLoader] pointer publication refused for ${resolvedSiteId}/${cleanSed.id}: ${committed?.reason ?? 'uncommitted'}`,
        );
        return {
          ok: false,
          storagePath: stagingTargetPath,
          version: cleanSed.version,
          sed: cleanSed,
          error: `SED pointer publication refused (${committed?.reason ?? 'uncommitted'}).`,
        };
      }
      if (
        committed.sedId !== cleanSed.id ||
        typeof committed.sedVersion !== 'number' ||
        !Number.isFinite(committed.sedVersion) ||
        typeof committed.sedStoragePath !== 'string' ||
        committed.sedStoragePath.trim() !== stagedPath ||
        typeof committed.sedStorageGeneration !== 'string' ||
        committed.sedStorageGeneration.trim() !== stagedGeneration ||
        typeof committed.sedContentHash !== 'string' ||
        !committed.sedContentHash.trim()
      ) {
        return {
          ok: false,
          storagePath: stagingTargetPath,
          version: cleanSed.version,
          sed: cleanSed,
          error: 'Malformed or incomplete pointer publication receipt.',
        };
      }
      committedPath = committed.sedStoragePath.trim();
      committedVersion = committed.sedVersion;
      committedGeneration = committed.sedStorageGeneration.trim();
      committedContentHash = committed.sedContentHash.trim();
    } else {
      const casRes = await authedFetch(
        `/api/sites/${encodeURIComponent(resolvedSiteId)}/sed-pointer-cas`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ownerUid: resolvedOwnerUid,
            operationId: pubOperationId,
            candidateOperationId: stagedOperationId,
            expected: baselinePointer,
            patch: pointerPatch,
            options: {
              ...(input.statusPatch ? { statusPatch: input.statusPatch } : {}),
              ...(input.requireIdle !== undefined ? { requireIdle: input.requireIdle } : {}),
            },
          }),
        },
      );
      if (!casRes.ok) {
        clearEnvelopeIfTerminal(casRes.status);
        const errText = await casRes.text().catch(() => '');
        console.warn(
          `[sedLoader] sed-pointer-cas failed (${casRes.status}) for ${resolvedSiteId}: ${errText.slice(0, 200)}`,
        );
        return {
          ok: false,
          storagePath: stagingTargetPath,
          version: cleanSed.version,
          sed: cleanSed,
          status: casRes.status,
          error: `SED pointer publication failed (HTTP ${casRes.status}): ${errText.slice(0, 200)}`,
        };
      }
      let casBody: Record<string, unknown> | null = null;
      try {
        const rawCas = typeof casRes.text === 'function' ? await casRes.text() : '';
        casBody = rawCas
          ? (JSON.parse(rawCas) as Record<string, unknown>)
          : typeof casRes.json === 'function'
            ? ((await casRes.json()) as Record<string, unknown>)
            : null;
      } catch {
        try {
          casBody =
            typeof casRes.json === 'function'
              ? ((await casRes.json()) as Record<string, unknown>)
              : null;
        } catch {
          casBody = null;
        }
      }
      if (!casBody || casBody.committed !== true) {
        if (casBody && casBody.committed === false) {
          PENDING_STAGE_ENVELOPES.delete(retryEnvelopeKey);
        }
        const reason = typeof casBody?.reason === 'string' ? casBody.reason : 'uncommitted';
        console.warn(`[sedLoader] sed-pointer-cas returned committed=false (${reason}) for ${resolvedSiteId}`);
        return {
          ok: false,
          storagePath: stagingTargetPath,
          version: cleanSed.version,
          sed: cleanSed,
          error: `SED pointer publication did not commit (${reason}).`,
        };
      }
      if (
        casBody.operationId !== pubOperationId ||
        casBody.candidateOperationId !== stagedOperationId ||
        casBody.ownerUid !== resolvedOwnerUid ||
        casBody.siteId !== resolvedSiteId ||
        casBody.sedId !== cleanSed.id ||
        typeof casBody.sedVersion !== 'number' ||
        !Number.isFinite(casBody.sedVersion) ||
        typeof casBody.sedStoragePath !== 'string' ||
        casBody.sedStoragePath.trim() !== stagedPath ||
        typeof casBody.sedStorageGeneration !== 'string' ||
        casBody.sedStorageGeneration.trim() !== stagedGeneration ||
        typeof casBody.sedContentHash !== 'string' ||
        !casBody.sedContentHash.trim()
      ) {
        return {
          ok: false,
          storagePath: stagingTargetPath,
          version: cleanSed.version,
          sed: cleanSed,
          error: 'Malformed or incomplete pointer publication receipt.',
        };
      }
      committedPath = casBody.sedStoragePath.trim();
      committedVersion = casBody.sedVersion;
      committedGeneration = casBody.sedStorageGeneration.trim();
      committedContentHash = casBody.sedContentHash.trim();
    }

    // Load the committed immutable object from storage and verify identity/generation/hash
    // before priming cache or returning success.
    const loadRes = await authedFetch(
      `/api/storage/load?path=${encodeURIComponent(committedPath)}`,
    );
    if (!loadRes.ok) {
      return {
        ok: false,
        storagePath: stagingTargetPath,
        version: cleanSed.version,
        sed: cleanSed,
        status: loadRes.status,
        error: `Committed SED reload failed (HTTP ${loadRes.status}) for ${committedPath}.`,
      };
    }
    const loadedGenHeader = loadRes.headers?.get?.('x-storage-generation')?.trim();
    if (!loadedGenHeader || loadedGenHeader !== committedGeneration) {
      return {
        ok: false,
        storagePath: stagingTargetPath,
        version: cleanSed.version,
        sed: cleanSed,
        error: 'Committed SED reload generation header missing or mismatched.',
      };
    }
    let committedSed: SiteEvidenceDocument | null = null;
    try {
      const rawLoaded = typeof loadRes.text === 'function' ? await loadRes.text() : '';
      committedSed = (
        rawLoaded
          ? JSON.parse(rawLoaded)
          : typeof loadRes.json === 'function'
            ? await loadRes.json()
            : null
      ) as SiteEvidenceDocument | null;
    } catch {
      try {
        committedSed =
          typeof loadRes.json === 'function'
            ? ((await loadRes.json()) as SiteEvidenceDocument)
            : null;
      } catch {
        committedSed = null;
      }
    }
    if (
      !committedSed ||
      typeof committedSed !== 'object' ||
      committedSed.id !== cleanSed.id ||
      committedSed.version !== committedVersion ||
      computeSedContentHashV2(committedSed) !== committedContentHash
    ) {
      return {
        ok: false,
        storagePath: stagingTargetPath,
        version: cleanSed.version,
        sed: cleanSed,
        error: 'Committed SED reload body failed identity/version/hash verification.',
      };
    }

    PENDING_STAGE_ENVELOPES.delete(retryEnvelopeKey);

    const preserveRevisions = new Set<string>([committedPath]);
    if (
      baselinePointer.sedStoragePath &&
      parseSedRevisionStoragePath(baselinePointer.sedStoragePath, resolvedSiteId)
    ) {
      preserveRevisions.add(baselinePointer.sedStoragePath);
    }
    invalidateMutableSedCacheAliases(cleanSed.id, preserveRevisions);
    SED_GENERATION_CACHE.set(committedPath, committedGeneration);
    SED_GENERATION_CACHE.set(cleanSed.id, committedGeneration);
    const cacheUid = user?.uid || resolvedOwnerUid;
    if (cacheUid) {
      rememberSed(cacheUid, cleanSed.id, committedPath, committedVersion, committedSed);
    }
    return {
      ok: true,
      storagePath: committedPath,
      version: committedVersion,
      generation: committedGeneration,
      sed: committedSed,
    };
  } catch (e) {
    console.warn('[sedLoader] stageAndPublishSedDocument threw:', (e as Error)?.message);
    return {
      ok: false,
      storagePath: stagingTargetPath,
      version: cleanSed.version,
      sed: cleanSed,
      error: (e as Error)?.message ?? 'Unknown SED save error',
    };
  }
}

export async function saveSedById(
  sed: SiteEvidenceDocument,
  storagePath?: string,
  options?: { operationId?: string; siteId?: string; ifGenerationMatch?: string },
): Promise<boolean> {
  const user = auth.currentUser;
  if (!user || !sed?.id) return false;
  const result = await stageAndPublishSedDocument({
    sed,
    storagePath,
    ownerUid: user.uid,
    ...(options?.operationId ? { operationId: options.operationId } : {}),
    ...(options?.siteId ? { siteId: options.siteId } : {}),
    ...(options?.ifGenerationMatch ? { ifGenerationMatch: options.ifGenerationMatch } : {}),
  });
  return result.ok;
}

/**
 * Find the most recent SED for a site by looking at its reports.
 * Returns { sedId, sedText } or null if no reports exist.
 */
export function findLatestSedForSite(
  siteId: string,
  reports: { id: string; siteId?: string; siteName: string; sedId?: string; sedText?: string; createdAt: string }[],
  siteName: string,
): { sedId: string; sedText?: string } | null {
  const siteReports = reports
    .filter(r => reportBelongsToSite({ id: siteId, name: siteName }, r))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  for (const r of siteReports) {
    if (r.sedId) return { sedId: r.sedId, sedText: r.sedText };
  }
  return null;
}
